import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Building2, Mail, Phone, MapPin, Trash2, Edit, CheckCircle, XCircle, Users, ChevronDown, ChevronRight, AlertCircle, CreditCard, Building, Power, PowerOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Agencies = () => {
  const [agencies, setAgencies] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [expandedAgency, setExpandedAgency] = useState(null);
  const [agencyUsers, setAgencyUsers] = useState({});
  const [companyEmail, setCompanyEmail] = useState('accounting@viphandling.com');
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [editPasswordDialog, setEditPasswordDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    name: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    post_code: "",
    country: "",
    vat_number: "",
    bank_name: "",
    bank_account: "",
    iban: "",
    swift_bic: "",
    has_2h_assistance: true,
    has_3h_assistance: true
  });
  const [editingAgency, setEditingAgency] = useState(null);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [selectedAgencyForUser, setSelectedAgencyForUser] = useState(null);
  const [newUserData, setNewUserData] = useState({
    email: '',
    full_name: '',
    password: ''
  });
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAgencyUser = currentUser.role === 'agency';
  const isAdmin = currentUser.role === 'admin';

  useEffect(() => {
    fetchAgencies();
    fetchCompanySettings();
    if (isAdmin) {
      fetchPendingUsers();
    }
  }, []);

  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If agency user, filter to show only their agency
      if (isAgencyUser && currentUser.agency_id) {
        const userAgency = response.data.find(a => a.id === currentUser.agency_id);
        setAgencies(userAgency ? [userAgency] : []);
      } else {
        // Show all agencies (pending and approved) sorted alphabetically by name
        const sortedAgencies = response.data.sort((a, b) => 
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
        setAgencies(sortedAgencies);
      }
    } catch (error) {
      toast.error("Failed to fetch agencies");
    }
    setLoading(false);
  };

  const fetchCompanySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.email) {
        setCompanyEmail(response.data.email);
      }
    } catch (error) {
      console.error("Failed to fetch company settings");
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only agency users
      const agencyPendingUsers = response.data.filter(u => u.role === 'agency');
      setPendingUsers(agencyPendingUsers);
    } catch (error) {
      console.error("Failed to fetch pending users");
    }
  };


  const fetchAgencyUsers = async (agencyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies/${agencyId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Backend returns {agency, users, total_users}
      const responseData = response.data;
      
      // Ensure users is an array
      let users = Array.isArray(responseData.users) ? responseData.users : [];
      
      // If logged in as agency, only show current user
      if (currentUser.role === 'agency' && currentUser.email) {
        const currentEmail = currentUser.email.toLowerCase().trim();
        users = users.filter(u => {
          if (!u || !u.email) return false;
          const userEmail = u.email.toLowerCase().trim();
          return userEmail === currentEmail;
        });
      }
      
      // Store in the format expected by the UI
      setAgencyUsers(prev => ({
        ...prev,
        [agencyId]: {
          users: users,
          total_users: users.length
        }
      }));
    } catch (error) {
      console.error('Fetch agency users error:', error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to fetch agency users";
      toast.error(errorMessage);
      // Set empty structure on error
      setAgencyUsers(prev => ({
        ...prev,
        [agencyId]: { users: [], total_users: 0 }
      }));
    }
  };

  const toggleAgency = (agencyId) => {
    if (expandedAgency === agencyId) {
      setExpandedAgency(null);
    } else {
      setExpandedAgency(agencyId);
      if (!agencyUsers[agencyId]) {
        fetchAgencyUsers(agencyId);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingAgency ? `${API}/agencies/${editingAgency}` : `${API}/agencies`;
      const method = editingAgency ? 'put' : 'post';
      
      console.log('Submitting agency:', method.toUpperCase(), url);
      console.log('Form data:', formData);
      
      const response = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Response:', response.data);
      
      toast.success(editingAgency ? "Agency updated successfully" : "Agency created successfully");
      setOpen(false);
      setEditingAgency(null);
      setFormData({ name: "", contact_person: "", email: "", phone: "", address: "", city: "", post_code: "", country: "", vat_number: "", bank_name: "", bank_account: "", iban: "", swift_bic: "" });
      fetchAgencies();
    } catch (error) {
      console.error('Agency save error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to save agency");
    }
  };

  const handleEdit = (agency) => {
    if (isAgencyUser) {
      setShowContactPopup(true);
      return;
    }
    setEditingAgency(agency.id);
    setFormData({
      name: agency.name,
      contact_person: agency.contact_person,
      email: agency.email,
      phone: agency.phone,
      address: agency.address || "",
      city: agency.city || "",
      post_code: agency.post_code || "",
      country: agency.country || "",
      vat_number: agency.vat_number || "",
      bank_name: agency.bank_name || "",
      bank_account: agency.bank_account || "",
      iban: agency.iban || "",
      swift_bic: agency.swift_bic || "",
      has_2h_assistance: agency.has_2h_assistance !== undefined ? agency.has_2h_assistance : true,
      has_3h_assistance: agency.has_3h_assistance !== undefined ? agency.has_3h_assistance : true
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (isAgencyUser) {
      setShowContactPopup(true);
      return;
    }
    if (!window.confirm('Are you sure you want to delete this agency?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/agencies/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Agency deleted successfully");
      fetchAgencies();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to delete agency";
      toast.error(errorMessage, { duration: 6000 });
    }
  };

  const handleToggleActive = async (agency) => {
    if (isAgencyUser) {
      setShowContactPopup(true);
      return;
    }
    const isCurrentlyActive = agency.is_active !== false;
    const action = isCurrentlyActive ? 'deactivate' : 'activate';
    if (!window.confirm(
      `Are you sure you want to ${action} "${agency.name}"?\n\n` +
      (isCurrentlyActive
        ? 'This agency will be hidden from invoicing and other filter dropdowns. Existing data (flights, invoices) will be preserved.'
        : 'This agency will become available again in all filters and dropdowns.')
    )) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/agencies/${agency.id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Agency ${isCurrentlyActive ? 'deactivated' : 'activated'} successfully`);
      fetchAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} agency`);
    }
  };

  const handleApprove = async (agency) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/agencies/${agency.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Agency approved successfully! Notification email sent.");
      fetchAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve agency");
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Are you sure you want to reject this agency registration?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/agencies/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Agency registration rejected");
      fetchAgencies();
    } catch (error) {
      toast.error("Failed to reject agency");
    }
  };

  const openAddDialog = () => {
    setEditingAgency(null);
    setFormData({ name: "", contact_person: "", email: "", phone: "", address: "", city: "", post_code: "", country: "", vat_number: "", bank_name: "", bank_account: "", iban: "", swift_bic: "" });
    setOpen(true);
  };

  const handleBlockUser = async (userId, agencyId) => {
    if (!window.confirm('Are you sure you want to block this user? They will not be able to log in.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/${userId}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User access blocked");
      // Refresh user list
      fetchAgencyUsers(agencyId);
    } catch (error) {
      toast.error("Failed to block user");
    }
  };

  const handleUnblockUser = async (userId, agencyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/${userId}/unblock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User access unblocked");
      // Refresh user list
      fetchAgencyUsers(agencyId);
    } catch (error) {
      toast.error("Failed to unblock user");
    }
  };

  const handleEditUserPassword = (user) => {
    setEditingUser(user);
    setNewPassword('');
    setEditPasswordDialog(true);
  };

  const handleSavePassword = async () => {
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/${editingUser.id}/change-password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password updated successfully");
      setEditPasswordDialog(false);
      setEditingUser(null);
      setNewPassword('');
      // Refresh user list to show updated password
      if (expandedAgency) {
        fetchAgencyUsers(expandedAgency);
      }
    } catch (error) {
      toast.error("Failed to update password");
    }
  };

  const handleDeleteUser = async (userId, agencyId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted successfully");
      // Refresh user list
      fetchAgencyUsers(agencyId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleApproveUser = async (userId, agencyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User approved successfully");
      // Refresh user list
      fetchAgencyUsers(agencyId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve user");
    }
  };

  const handleDeclineUser = async (userId, agencyId) => {
    if (!window.confirm('Are you sure you want to decline this user registration?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/${userId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User registration declined");
      // Refresh user list
      fetchAgencyUsers(agencyId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline user");
    }
  };


  const handleCreateUserForAgency = (agency) => {
    setSelectedAgencyForUser(agency);
    const emailDomain = agency.email.split('@')[1];
    setNewUserData({
      email: `@${emailDomain}`,
      full_name: '',
      password: ''
    });
    setShowCreateUserDialog(true);
  };

  const handleSubmitNewUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/register`, {
        ...newUserData,
        role: 'agency',
        agency_id: selectedAgencyForUser.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User created successfully");
      setShowCreateUserDialog(false);
      setNewUserData({ email: '', full_name: '', password: '' });
      fetchAgencyUsers(selectedAgencyForUser.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };


  const handleEditDeleteAttempt = () => {
    setShowContactPopup(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: '#C0C0C0' }}>Loading agencies...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFD700' }}>
            {isAgencyUser ? 'My Agency' : 'Agencies'}
          </h1>
          <p style={{ color: '#C0C0C0' }}>
            {isAgencyUser 
              ? 'View your agency information and users'
              : 'Manage agencies and their users'
            }
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={openAddDialog}
            style={{ background: '#FFD700', color: '#0A1128' }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Agency
          </Button>
        )}
      </div>

      {/* Pending Agencies Section */}
      {!isAgencyUser && agencies.filter(a => a.status === 'pending_approval').length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFA500' }}>
            Pending Approvals ({agencies.filter(a => a.status === 'pending_approval').length})
          </h2>
          <div className="space-y-4">
            {agencies.filter(a => a.status === 'pending_approval').map((agency) => (
              <Card
                key={agency.id}
                className="border-2"
                style={{
                  background: 'rgba(255, 165, 0, 0.1)',
                  borderColor: '#FFA500'
                }}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Building2 size={24} style={{ color: '#FFA500' }} />
                      <div>
                        <CardTitle style={{ color: '#FFFFFF' }}>{agency.name}</CardTitle>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>Pending Approval</p>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(agency)}
                          style={{ background: '#4ADE80', color: '#0A1128' }}
                        >
                          <CheckCircle size={16} className="mr-2" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(agency.id)}
                          style={{ borderColor: '#EF4444', color: '#EF4444' }}
                        >
                          <XCircle size={16} className="mr-2" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Contact Person</p>
                      <p style={{ color: '#FFFFFF' }}>{agency.contact_person}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Email</p>
                      <p style={{ color: '#FFFFFF' }}>{agency.email}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Phone</p>
                      <p style={{ color: '#FFFFFF' }}>{agency.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Address</p>
                      <p style={{ color: '#FFFFFF' }}>
                        {agency.address && <>{agency.address}<br /></>}
                        {agency.post_code && agency.city ? (
                          <>{agency.post_code} {agency.city}{agency.country ? ` / ${agency.country}` : ''}</>
                        ) : (
                          (agency.city || agency.post_code || agency.country || 'N/A')
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Pending Agency User Requests Section */}
      {!isAgencyUser && pendingUsers.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#F59E0B' }}>
            <Users className="inline mr-2 h-6 w-6" />
            Pending Agency User Requests ({pendingUsers.length})
          </h2>
          <div className="space-y-4">
            {pendingUsers.map((user) => (
              <Card
                key={user.id}
                className="border-2"
                style={{
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderColor: '#F59E0B'
                }}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Users size={24} style={{ color: '#F59E0B' }} />
                      <div>
                        <CardTitle style={{ color: '#FFFFFF' }}>{user.full_name}</CardTitle>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>
                          Pending Agency User Registration
                        </p>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            handleApproveUser(user.id, user.agency_id);
                            fetchPendingUsers();
                          }}
                          style={{ background: '#4ADE80', color: '#0A1128' }}
                        >
                          <CheckCircle size={16} className="mr-2" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            handleDeclineUser(user.id, user.agency_id);
                            fetchPendingUsers();
                          }}
                          style={{ borderColor: '#EF4444', color: '#EF4444' }}
                        >
                          <XCircle size={16} className="mr-2" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Email</p>
                      <p style={{ color: '#FFFFFF' }}>{user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Role</p>
                      <p style={{ color: '#FFFFFF' }}>Agency User</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Agency ID</p>
                      <p style={{ color: '#FFFFFF' }}>{user.agency_id || 'Not assigned'}</p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#B0B0B0' }}>Registration Date</p>
                      <p style={{ color: '#FFFFFF' }}>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}


      {/* Approved Agencies Section */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ color: '#FFD700' }}>
          {isAgencyUser ? '' : `Approved Agencies (${agencies.filter(a => a.status === 'approved').length})`}
        </h2>
        {agencies.filter(a => a.status === 'approved').map((agency) => {
          const isExpanded = expandedAgency === agency.id;
          const usersData = agencyUsers[agency.id];

          return (
            <Card
              key={agency.id}
              className="transition-all hover:shadow-lg"
              style={{
                background: 'rgba(30, 42, 74, 0.6)',
                borderColor: 'rgba(74, 90, 138, 0.4)'
              }}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <Building2 size={24} style={{ color: '#FFD700' }} />
                    <div className="flex-1">
                      <CardTitle style={{ color: '#FFFFFF', fontSize: '1.25rem' }}>
                        {agency.name}
                        {agency.is_active === false && (
                          <span
                            className="ml-3 px-2 py-0.5 rounded text-xs font-semibold"
                            style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#EF4444', border: '1px solid #EF4444' }}
                            data-testid={`agency-status-inactive-${agency.id}`}
                          >
                            INACTIVE
                          </span>
                        )}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Mail size={14} style={{ color: '#B0B0B0' }} />
                        <span className="text-sm" style={{ color: '#B0B0B0' }}>
                          {agency.email}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleAgency(agency.id)}
                      style={{
                        borderColor: '#FFD700',
                        color: '#FFD700',
                        background: 'transparent'
                      }}
                    >
                      <Users size={16} className="mr-2" />
                      {isExpanded ? 'Hide Users' : 'View Users'}
                      {isExpanded ? <ChevronDown size={16} className="ml-2" /> : <ChevronRight size={16} className="ml-2" />}
                    </Button>
                    
                    {!isAgencyUser && isAdmin && isExpanded && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCreateUserForAgency(agency)}
                        style={{
                          borderColor: '#22C55E',
                          color: '#22C55E',
                          background: 'transparent'
                        }}
                      >
                        <Plus size={16} className="mr-2" />
                        Create User
                      </Button>
                    )}

                    {!isAgencyUser && isAdmin && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleActive(agency)}
                          data-testid={`agency-toggle-active-${agency.id}`}
                          style={{
                            borderColor: agency.is_active === false ? '#22C55E' : '#F59E0B',
                            color: agency.is_active === false ? '#22C55E' : '#F59E0B',
                            background: 'transparent'
                          }}
                        >
                          {agency.is_active === false ? (
                            <>
                              <Power size={16} className="mr-2" />
                              Activate
                            </>
                          ) : (
                            <>
                              <PowerOff size={16} className="mr-2" />
                              Deactivate
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(agency)}
                          style={{
                            borderColor: '#60A5FA',
                            color: '#60A5FA',
                            background: 'transparent'
                          }}
                        >
                          <Edit size={16} className="mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(agency.id)}
                          style={{
                            borderColor: '#EF4444',
                            color: '#EF4444',
                            background: 'transparent'
                          }}
                        >
                          <Trash2 size={16} className="mr-2" />
                          Delete
                        </Button>
                      </>
                    )}

                    {isAgencyUser && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditDeleteAttempt}
                        style={{
                          borderColor: '#C0C0C0',
                          color: '#C0C0C0',
                          background: 'transparent'
                        }}
                      >
                        <Edit size={16} className="mr-2" />
                        Request Edit
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              {/* Agency Full Details - Always Visible */}
              <CardContent className="border-t" style={{ borderColor: 'rgba(74, 90, 138, 0.3)' }}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {/* Contact Information */}
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#FFD700' }}>Contact Person</p>
                    <div className="flex items-center gap-2">
                      <Users size={14} style={{ color: '#B0B0B0' }} />
                      <p className="text-sm" style={{ color: '#FFFFFF' }}>{agency.contact_person || 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#FFD700' }}>Phone</p>
                    <div className="flex items-center gap-2">
                      <Phone size={14} style={{ color: '#B0B0B0' }} />
                      <p className="text-sm" style={{ color: '#FFFFFF' }}>{agency.phone || 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#FFD700' }}>Address</p>
                    <div className="flex items-start gap-2">
                      <MapPin size={14} style={{ color: '#B0B0B0', marginTop: '2px' }} />
                      <div className="text-sm" style={{ color: '#FFFFFF' }}>
                        {agency.address && <div>{agency.address}</div>}
                        {(agency.post_code || agency.city || agency.country) && (
                          <div>
                            {agency.post_code && <span>{agency.post_code} </span>}
                            {agency.city && <span>{agency.city}</span>}
                            {agency.country && <span> / {agency.country}</span>}
                          </div>
                        )}
                        {!agency.address && !agency.city && !agency.post_code && !agency.country && 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* VAT Information */}
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#FFD700' }}>VAT Number</p>
                    <div className="flex items-center gap-2">
                      <CreditCard size={14} style={{ color: '#B0B0B0' }} />
                      <p className="text-sm" style={{ color: '#FFFFFF' }}>{agency.vat_number || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Bank Information */}
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#FFD700' }}>Bank Name</p>
                    <div className="flex items-center gap-2">
                      <Building size={14} style={{ color: '#B0B0B0' }} />
                      <p className="text-sm" style={{ color: '#FFFFFF' }}>{agency.bank_name || 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#FFD700' }}>Bank Account</p>
                    <div className="flex items-center gap-2">
                      <CreditCard size={14} style={{ color: '#B0B0B0' }} />
                      <p className="text-sm" style={{ color: '#FFFFFF' }}>{agency.bank_account || 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#FFD700' }}>IBAN</p>
                    <div className="flex items-center gap-2">
                      <CreditCard size={14} style={{ color: '#B0B0B0' }} />
                      <p className="text-sm" style={{ color: '#FFFFFF' }}>{agency.iban || 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#FFD700' }}>SWIFT/BIC</p>
                    <div className="flex items-center gap-2">
                      <Building size={14} style={{ color: '#B0B0B0' }} />
                      <p className="text-sm" style={{ color: '#FFFFFF' }}>{agency.swift_bic || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Service Preferences */}
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#FFD700' }}>2h Assistance</p>
                    <p className="text-sm" style={{ color: agency.has_2h_assistance ? '#22C55E' : '#EF4444' }}>
                      {agency.has_2h_assistance ? '✓ Enabled' : '✗ Disabled'}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: '#FFD700' }}>3h Assistance</p>
                    <p className="text-sm" style={{ color: agency.has_3h_assistance ? '#22C55E' : '#EF4444' }}>
                      {agency.has_3h_assistance ? '✓ Enabled' : '✗ Disabled'}
                    </p>
                  </div>
                </div>
              </CardContent>

              {isExpanded && (
                <CardContent className="border-t" style={{ borderColor: 'rgba(74, 90, 138, 0.3)' }}>
                  {usersData && usersData.users && Array.isArray(usersData.users) ? (
                    <>
                      <div className="mb-4 p-3 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                        <p className="text-sm" style={{ color: '#FFD700' }}>
                          <strong>{usersData.total_users || 0}</strong> user(s) with <strong>@{agency.email.split('@')[1]}</strong> email domain
                        </p>
                      </div>

                      {usersData.users.length > 0 ? (
                        <div className="space-y-3">
                          {usersData.users.map((user) => (
                            <div
                              key={user.id}
                              className="p-4 rounded border"
                              style={{
                                background: 'rgba(10, 17, 40, 0.5)',
                                borderColor: user.blocked ? 'rgba(239, 68, 68, 0.5)' : 'rgba(74, 90, 138, 0.3)'
                              }}
                            >
                              {user.blocked && (
                                <div className="mb-3 p-2 rounded text-center" style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid #EF4444' }}>
                                  <p className="text-sm font-semibold" style={{ color: '#EF4444' }}>
                                    🚫 ACCESS BLOCKED
                                  </p>
                                </div>
                              )}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm" style={{ color: '#B0B0B0' }}>Name</p>
                                  <p className="font-medium" style={{ color: '#FFFFFF' }}>{user.full_name}</p>
                                </div>
                                <div>
                                  <p className="text-sm" style={{ color: '#B0B0B0' }}>Email</p>
                                  <p className="font-medium" style={{ color: '#FFFFFF' }}>{user.email}</p>
                                </div>
                                <div>
                                  <p className="text-sm" style={{ color: '#B0B0B0' }}>Role</p>
                                  <p className="font-medium capitalize" style={{ color: '#FFD700' }}>{user.role}</p>
                                </div>
                                <div>
                                  <p className="text-sm" style={{ color: '#B0B0B0' }}>Status</p>
                                  <p className="font-medium capitalize" style={{ 
                                    color: user.status === 'approved' ? '#4ADE80' : '#FFA500'
                                  }}>
                                    {user.status} {user.blocked && '(Blocked)'}
                                  </p>
                                </div>
                                <div className="col-span-2">
                                  <p className="text-sm" style={{ color: '#B0B0B0' }}>Password</p>
                                  <p className="font-mono text-sm p-2 rounded" style={{ 
                                    background: 'rgba(255, 215, 0, 0.1)',
                                    color: '#FFD700',
                                    border: '1px solid rgba(255, 215, 0, 0.3)'
                                  }}>
                                    {user.password !== 'N/A' ? user.password : 'Hidden (already hashed)'}
                                  </p>
                                </div>
                                
                                {/* Admin Actions */}
                                {isAdmin && (
                                  <div className="col-span-2 flex gap-2 mt-2 pt-3 border-t" style={{ borderColor: 'rgba(74, 90, 138, 0.3)' }}>
                                    {/* Approve/Decline for Pending Users */}
                                    {user.status === 'pending_approval' && (
                                      <>
                                        <Button
                                          size="sm"
                                          onClick={() => handleApproveUser(user.id, agency.id)}
                                          style={{ background: '#4ADE80', color: '#0A1128' }}
                                        >
                                          <CheckCircle size={14} className="mr-1" />
                                          Approve User
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleDeclineUser(user.id, agency.id)}
                                          style={{ borderColor: '#EF4444', color: '#EF4444' }}
                                        >
                                          <XCircle size={14} className="mr-1" />
                                          Decline
                                        </Button>
                                      </>
                                    )}
                                    
                                    {/* Block/Unblock for Approved Users */}
                                    {user.status === 'approved' && (
                                      <>
                                        {user.blocked ? (
                                          <Button
                                            size="sm"
                                            onClick={() => handleUnblockUser(user.id, agency.id)}
                                            style={{ background: '#4ADE80', color: '#0A1128' }}
                                          >
                                            Unblock Access
                                          </Button>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleBlockUser(user.id, agency.id)}
                                            style={{ borderColor: '#EF4444', color: '#EF4444' }}
                                          >
                                            Block Access
                                          </Button>
                                        )}
                                      </>
                                    )}
                                    
                                    {/* Password Edit - Admin */}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleEditUserPassword(user)}
                                      style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
                                    >
                                      Edit Password
                                    </Button>
                                    
                                    {/* Delete Button (Admin Only) */}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleDeleteUser(user.id, agency.id)}
                                      style={{ borderColor: '#EF4444', color: '#EF4444' }}
                                    >
                                      <Trash2 size={14} className="mr-1" />
                                      Delete User
                                    </Button>
                                  </div>
                                )}
                                
                                {/* Password Edit for Current Agency User Only */}
                                {!isAdmin && user.email && currentUser.email && 
                                 user.email.toLowerCase().trim() === currentUser.email.toLowerCase().trim() && (
                                  <div className="col-span-2 flex gap-2 mt-2 pt-3 border-t" style={{ borderColor: 'rgba(74, 90, 138, 0.3)' }}>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleEditUserPassword(user)}
                                      style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
                                    >
                                      Edit My Password
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center py-4" style={{ color: '#B0B0B0' }}>
                          No users found for this agency
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <p style={{ color: '#C0C0C0' }}>Loading users...</p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

        {agencies.length === 0 && (
          <Card style={{ background: 'rgba(30, 42, 74, 0.6)', borderColor: 'rgba(74, 90, 138, 0.4)' }}>
            <CardContent className="py-12 text-center">
              <Building2 size={48} style={{ color: '#B0B0B0', margin: '0 auto 1rem' }} />
              <p style={{ color: '#C0C0C0' }}>No agencies found</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent style={{ background: 'rgba(30, 42, 74, 0.95)', borderColor: 'rgba(74, 90, 138, 0.4)' }} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>
              {editingAgency ? 'Edit Agency' : 'Add New Agency'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#E8E8E8' }}>Agency Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Contact Person *</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  required
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div className="col-span-2">
                <Label style={{ color: '#E8E8E8' }}>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                  placeholder="Street address"
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Post Code</Label>
                <Input
                  value={formData.post_code}
                  onChange={(e) => setFormData({ ...formData, post_code: e.target.value })}
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Country</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>VAT Number</Label>
                <Input
                  value={formData.vat_number}
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Bank Name</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Account Number</Label>
                <Input
                  value={formData.bank_account}
                  onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>IBAN</Label>
                <Input
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>SWIFT/BIC</Label>
                <Input
                  value={formData.swift_bic}
                  onChange={(e) => setFormData({ ...formData, swift_bic: e.target.value })}
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
              </div>
            </div>
            
            {/* Basic Assistance Settings */}
            <div className="space-y-4 p-4 rounded" style={{ background: 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
              <Label style={{ color: '#FFD700', fontSize: '1rem', fontWeight: '600' }}>Basic Assistance Services</Label>
              <p className="text-sm" style={{ color: '#B0B0B0' }}>Select which Basic Assistance service durations this agency can use</p>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="has_2h_assistance"
                  checked={formData.has_2h_assistance}
                  onChange={(e) => setFormData({ ...formData, has_2h_assistance: e.target.checked })}
                  className="w-4 h-4"
                  style={{ accentColor: '#60A5FA' }}
                />
                <Label htmlFor="has_2h_assistance" style={{ color: '#E8E8E8' }}>2 Hour Service</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="has_3h_assistance"
                  checked={formData.has_3h_assistance}
                  onChange={(e) => setFormData({ ...formData, has_3h_assistance: e.target.checked })}
                  className="w-4 h-4"
                  style={{ accentColor: '#22C55E' }}
                />
                <Label htmlFor="has_3h_assistance" style={{ color: '#E8E8E8' }}>3 Hour Service</Label>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                type="submit"
                className="flex-1"
                style={{ background: '#FFD700', color: '#0A1128' }}
              >
                {editingAgency ? 'Update Agency' : 'Create Agency'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                style={{ borderColor: '#C0C0C0', color: '#C0C0C0' }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>



      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>
              <Plus className="inline mr-2" />
              Create User for {selectedAgencyForUser?.name}
            </DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Create a new user account for this agency
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitNewUser} className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Email *</Label>
              <Input
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                required
                placeholder={`user@${selectedAgencyForUser?.email.split('@')[1]}`}
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
              />
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                Must use @{selectedAgencyForUser?.email.split('@')[1]} domain
              </p>
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Full Name *</Label>
              <Input
                type="text"
                value={newUserData.full_name}
                onChange={(e) => setNewUserData({ ...newUserData, full_name: e.target.value })}
                required
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
              />
            </div>
            <div>
              <Label style={{ color: '#E8E8E8' }}>Password *</Label>
              <Input
                type="text"
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                required
                minLength={6}
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
              />
              <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Minimum 6 characters</p>
            </div>
            <div className="flex gap-2">
              <Button type="submit" style={{ background: '#22C55E', color: '#FFFFFF' }}>
                Create User
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateUserDialog(false)}
                style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Popup for Agency Users */}
      <Dialog open={showContactPopup} onOpenChange={setShowContactPopup}>
        <DialogContent style={{ background: 'rgba(30, 42, 74, 0.95)', borderColor: 'rgba(74, 90, 138, 0.4)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={24} />
              Contact VIP Handling
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p style={{ color: '#E8E8E8' }}>
              For all changes to your agency information, please contact VIP Handling by email:
            </p>
            <div className="p-4 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
              <p className="font-mono text-lg text-center" style={{ color: '#FFD700' }}>
                {companyEmail}
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => setShowContactPopup(false)}
              style={{ background: '#FFD700', color: '#0A1128' }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Password Dialog */}
      <Dialog open={editPasswordDialog} onOpenChange={setEditPasswordDialog}>
        <DialogContent style={{ background: 'rgba(30, 42, 74, 0.95)', borderColor: 'rgba(74, 90, 138, 0.4)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>
              Edit User Password
            </DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div className="p-3 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                <p className="text-sm" style={{ color: '#B0B0B0' }}>User</p>
                <p style={{ color: '#FFFFFF' }}>{editingUser.full_name}</p>
                <p className="text-sm" style={{ color: '#B0B0B0' }}>{editingUser.email}</p>
              </div>
              
              <div>
                <Label style={{ color: '#E8E8E8' }}>New Password *</Label>
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: 'rgba(74, 90, 138, 0.4)', color: '#FFFFFF' }}
                />
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                  This password will be visible to the user and stored for admin reference
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSavePassword}
                  className="flex-1"
                  style={{ background: '#FFD700', color: '#0A1128' }}
                >
                  Save Password
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditPasswordDialog(false);
                    setEditingUser(null);
                    setNewPassword('');
                  }}
                  style={{ borderColor: '#C0C0C0', color: '#C0C0C0' }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Agencies;
